import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Moon, Sun, Shield, LogOut, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import CompanyApps from './CompanyApps';
import AdminPanel from './AdminPanel';
import axios from 'axios';
import axiosInstance from '@/utils/axiosConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyArea = ({ onBack }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [isFirstUser, setIsFirstUser] = useState(false);
  const [user, setUser] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [loading, setLoading] = useState(false);

  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });

  const [registerForm, setRegisterForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    full_name: ''
  });

  useEffect(() => {
    checkFirstUser();
    checkExistingAuth();
  }, []);

  const checkFirstUser = async () => {
    try {
      const response = await axios.get(`${API}/check-first-user`);
      setIsFirstUser(response.data.is_first_user);
      if (response.data.is_first_user) {
        setShowRegister(true);
      }
    } catch (error) {
      console.error('Error checking first user:', error);
    }
  };

  const checkExistingAuth = async () => {
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');
    if (token && userData) {
      try {
        // Verify token is still valid
        await axiosInstance.get('/verify-token');
        setUser(JSON.parse(userData));
        setIsAuthenticated(true);
      } catch (error) {
        // Token invalid, clear storage
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        console.error('Token verification failed:', error);
      }
    }
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/login`, loginForm);
      const { access_token, user } = response.data;
      
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('user_data', JSON.stringify(user));
      
      setUser(user);
      setIsAuthenticated(true);
      toast.success(`Bine ai venit, ${user.full_name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la autentificare');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Parolele nu se potrivesc!');
      return;
    }

    if (registerForm.password.length < 6) {
      toast.error('Parola trebuie să aibă cel puțin 6 caractere!');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...dataToSend } = registerForm;
      const response = await axios.post(`${API}/register`, dataToSend);
      const { access_token, user } = response.data;
      
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('user_data', JSON.stringify(user));
      
      setUser(user);
      setIsAuthenticated(true);
      
      if (isFirstUser) {
        toast.success(`Bine ai venit, ${user.full_name}! Ești administratorul suprem.`);
      } else {
        toast.success(`Cont creat cu succes! Bine ai venit, ${user.full_name}!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la înregistrare');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    setUser(null);
    setIsAuthenticated(false);
    toast.success('Deconectat cu succes!');
  };

  if (isAuthenticated && showAdminPanel) {
    return (
      <AdminPanel
        user={user}
        onBack={() => setShowAdminPanel(false)}
        onLogout={handleLogout}
      />
    );
  }

  if (isAuthenticated) {
    return (
      <CompanyApps
        user={user}
        onLogout={handleLogout}
        onBack={onBack}
        onAdminPanel={() => setShowAdminPanel(true)}
      />
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 right-1/3 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 py-8">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={onBack}
              className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center shadow-elegant-md">
              <Shield className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">Company Area</h1>
              <p className="text-xs text-muted-foreground">Zonă securizată</p>
            </div>
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={toggleDarkMode}
            className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-6 pb-12">
        <div className="max-w-md mx-auto">
          <Card className="bg-card border-border rounded-2xl p-8 shadow-elegant-lg">
            {isFirstUser && showRegister && (
              <div className="mb-6 p-4 bg-accent-light/50 border border-accent rounded-xl">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-accent mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Prima înregistrare</h3>
                    <p className="text-sm text-muted-foreground">
                      Vei deveni <strong>administrator suprem</strong> cu acces complet la toate funcțiile.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {showRegister ? 'Creare Cont' : 'Autentificare'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {showRegister
                  ? 'Completează datele pentru a crea un cont nou'
                  : 'Introdu datele tale de autentificare'}
              </p>
            </div>

            {showRegister ? (
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Nume complet</Label>
                  <Input
                    id="full_name"
                    type="text"
                    placeholder="Ion Popescu"
                    value={registerForm.full_name}
                    onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })}
                    required
                    className="rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="username">Nume utilizator</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="ionpopescu"
                    value={registerForm.username}
                    onChange={(e) => setRegisterForm({ ...registerForm, username: e.target.value })}
                    required
                    className="rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="ion.popescu@example.com"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                    required
                    className="rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Parolă</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    required
                    className="rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmă parola</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={registerForm.confirmPassword}
                    onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                    required
                    className="rounded-xl"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full rounded-xl"
                  variant="premium"
                  disabled={loading}
                >
                  {loading ? 'Se procesează...' : isFirstUser ? 'Creează Administrator Suprem' : 'Creare Cont'}
                </Button>

                {!isFirstUser && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full rounded-xl"
                    onClick={() => setShowRegister(false)}
                  >
                    Am deja cont
                  </Button>
                )}
              </form>
            ) : (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-username">Nume utilizator</Label>
                  <Input
                    id="login-username"
                    type="text"
                    placeholder="Nume utilizator"
                    value={loginForm.username}
                    onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                    required
                    className="rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password">Parolă</Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                    className="rounded-xl"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full rounded-xl"
                  variant="premium"
                  disabled={loading}
                >
                  {loading ? 'Se autentifică...' : 'Autentificare'}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full rounded-xl"
                  onClick={() => setShowRegister(true)}
                >
                  Creare cont nou
                </Button>
              </form>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
};

export default CompanyArea;
